import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "@/components/Toast";
import { FileWarning, ClipboardList, MapPin, Inbox, ArrowRight, Building2, AlertCircle, Truck, Package, Footprints } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { saludoMendoza, relojMendoza } from "@/lib/i18n";
import { NewsSeverityBadge } from "@/components/NewsSeverityBadge";
import { PassiveGuardsUpcoming } from "@/components/PassiveGuardsUpcoming";

const ACTIONS = [
  { to: "/supervisor/novedad", label: "Cargar novedad", desc: "Registrá un evento o incidente", icon: FileWarning, testId: "action-novedad" },
  { to: "/supervisor/visitas", label: "Cargar ingreso", desc: "Registrá entrada o salida de una visita", icon: Footprints, testId: "action-ingreso" },
  { to: "/supervisor/servicio", label: "Abrir / cerrar servicio", desc: "Marcá apertura y cierre por cliente", icon: ClipboardList, testId: "action-servicio" },
  { to: "/supervisor/checkin", label: "Marcación de visita", desc: "Ingreso/egreso a cliente con GPS", icon: MapPin, testId: "action-checkin" },
  { to: "/supervisor/vehiculos", label: "Vehículos", desc: "Registrá entrada/salida con patente", icon: Truck, testId: "action-vehiculos" },
  { to: "/supervisor/materiales", label: "Elementos de trabajo", desc: "Recepción/entrega con checklist", icon: Package, testId: "action-materiales" },
  // Fix AY (2026-09-30): subtítulo con los tipos que existen hoy (el de sueldo se sacó en AA).
  { to: "/supervisor/solicitud", label: "Solicitud interna", desc: "Materiales o cambio de personal", icon: Inbox, testId: "action-solicitud" },
];

export default function SupervisorHome() {
  const { user } = useAuth();
  const [news, setNews] = useState([]);
  const [openServices, setOpenServices] = useState([]);
  const [openCheckins, setOpenCheckins] = useState([]);
  const [requests, setRequests] = useState([]);
  const [clients, setClients] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [n, s, ck, r, c] = await Promise.all([
          api.get("/news"),
          api.get("/services?status=open"),
          api.get("/supervisor-checkins?active_only=true"),
          api.get("/internal-requests"),
          api.get("/clients"),
        ]);
        setNews(n.data.slice(0, 4));
        setOpenServices(s.data);
        setOpenCheckins(ck.data);
        setRequests(r.data);
        setClients(c.data);
      } catch (e) { toast.error(formatApiError(e)); }
    })();
  }, []);

  const nameOf = (cid) => clients.find((c) => c.id === cid)?.name || "—";
  const pendingCount = requests.filter((r) => r.status === "pendiente").length;
  const hasOpen = openServices.length > 0 || openCheckins.length > 0;
  const firstName = (user?.name || "").split(" ")[0] || "Supervisor";
  const saludo = saludoMendoza();
  const reloj = relojMendoza();

  return (
    <div style={{ padding: "8px 12px 0" }}>
      <div style={{ background: "linear-gradient(135deg, #0A0A0A 0%, #1a0e0f 100%)", color: "#FFFBF4", padding: "22px 18px", borderRadius: 4, marginBottom: 14, position: "relative", overflow: "hidden" }}>
        <div style={{ fontSize: 10, letterSpacing: "0.22em", color: "#BF3437", fontWeight: 800, textTransform: "uppercase", marginBottom: 6 }}>Supervisor · Mendoza · {reloj}</div>
        <h1 style={{ fontSize: 22, fontWeight: 900, lineHeight: 1.15, margin: 0, letterSpacing: "-0.02em" }}>
          {saludo}, <span style={{ color: "#BF3437" }}>{firstName}</span>.
        </h1>
        <p style={{ fontSize: 12, color: "rgba(255,251,244,0.62)", margin: "8px 0 0" }}>Lo que hagas hoy queda registrado y trazable para administración.</p>
        <div style={{ position: "absolute", right: -50, bottom: -20, width: 130, height: 130, background: "radial-gradient(circle, rgba(191,52,55,0.18), transparent 70%)" }} />
      </div>

      <PassiveGuardsUpcoming linkTo="/supervisor/guardias" />

      {/* "Lo que tenés en marcha" — contextual summary */}
      {hasOpen && (
        <div className="card" style={{ marginBottom: 14, padding: 14, borderLeft: "4px solid #BF3437" }} data-testid="open-summary">
          <div style={{ fontSize: 11, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.1em", color: "#BF3437", marginBottom: 8 }}>
            <AlertCircle size={11} style={{ display: "inline", marginRight: 4 }}/>En marcha ahora
          </div>
          {openServices.map((s) => (
            <Link key={s.id} to="/supervisor/servicio" style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: "1px solid var(--invict-bone-3)", textDecoration: "none", color: "inherit" }} data-testid={`open-svc-${s.id}`}>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <ClipboardList size={14} style={{ color: "#BF3437" }} />
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "#0A0A0A" }}>{nameOf(s.client_id)}</div>
                  <div style={{ fontSize: 10, color: "var(--invict-text-muted)" }}>Servicio abierto · {new Date(s.opened_at).toLocaleTimeString("es-AR", { hour: "2-digit", minute: "2-digit" })}</div>
                </div>
              </div>
              <ArrowRight size={14} style={{ color: "var(--invict-text-muted)", alignSelf: "center" }} />
            </Link>
          ))}
          {openCheckins.map((c) => (
            <Link key={c.id} to="/supervisor/checkin" style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: "1px solid var(--invict-bone-3)", textDecoration: "none", color: "inherit" }} data-testid={`open-ck-${c.id}`}>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <MapPin size={14} style={{ color: "#BF3437" }} />
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "#0A0A0A" }}>{nameOf(c.client_id)}</div>
                  <div style={{ fontSize: 10, color: "var(--invict-text-muted)" }}>Checkin abierto · {new Date(c.checkin_at).toLocaleTimeString("es-AR", { hour: "2-digit", minute: "2-digit" })}</div>
                </div>
              </div>
              <ArrowRight size={14} style={{ color: "var(--invict-text-muted)", alignSelf: "center" }} />
            </Link>
          ))}
        </div>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 22 }}>
        {ACTIONS.map(({ to, label, desc, icon: Ic, testId }) => (
          <Link key={to} to={to} className="btn-mobile-hero" data-testid={testId} style={{ textDecoration: "none", position: "relative" }}>
            <Ic size={22} style={{ color: "#BF3437" }} />
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 900, fontSize: 14 }}>{label}</div>
              <div style={{ fontSize: 11, fontWeight: 500, color: "var(--invict-text-muted)", textTransform: "none", letterSpacing: 0, marginTop: 2 }}>{desc}</div>
            </div>
            {testId === "action-solicitud" && pendingCount > 0 && (
              <span className="badge amber" style={{ marginRight: 6 }}>{pendingCount} pend.</span>
            )}
            <ArrowRight size={16} style={{ color: "var(--invict-text-muted)" }} />
          </Link>
        ))}
      </div>

      <h2 style={{ fontSize: 12, fontWeight: 800, letterSpacing: "0.1em", textTransform: "uppercase", color: "#0A0A0A", margin: "0 0 10px" }}>
        Mis novedades recientes
      </h2>
      <div className="card" style={{ padding: 0, overflow: "hidden", marginBottom: 16 }}>
        {news.length === 0 && <div style={{ padding: 20, textAlign: "center", color: "var(--invict-text-muted)", fontSize: 13 }}>Sin novedades cargadas todavía.</div>}
        {news.map((n) => (
          <div key={n.id} style={{ padding: "12px 16px", borderBottom: "1px solid var(--invict-bone-3)" }} data-testid={`news-${n.id}`}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
              <div style={{ fontWeight: 700, fontSize: 13, color: "#0A0A0A" }}>{n.title}</div>
              <NewsSeverityBadge news={n} />
            </div>
            <div style={{ fontSize: 11, color: "var(--invict-text-muted)", marginTop: 4 }}>
              <Building2 size={10} style={{ display: "inline", marginRight: 3 }}/>{nameOf(n.client_id)} · {new Date(n.created_at).toLocaleString("es-AR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
